import * as React from 'react'
import {Box, Typography, Alert, Chip, Button} from '@mui/material'
import {sectionCardSx, secondaryBtnSx} from '../../../../styles/patterns'
import {FONT, SIZE, WEIGHT} from '../../../../styles/foundation/typography'
import {NAVY, SUBTLE} from '../../../../styles/foundation/colors'
import {int, kg} from '../format'
import {combine} from './catalogueMath'
import {chipSx} from '../components/tone'

/**
 * Whether a candidate pair is already merged (`done`) or blocked because one
 * side already reports to a DIFFERENT mother than the other side of this
 * suggestion (`blocked`). Copied verbatim from the task brief's own spec —
 * exported and pure so this derivation is unit-tested without mounting
 * anything (see MergeSuggestions.test.js).
 */
export function candidateStatus(a, b) {
  const done = a.childOf === b.sku || b.childOf === a.sku ||
    (a.motherOf || []).indexOf(b.sku) >= 0 || (b.motherOf || []).indexOf(a.sku) >= 0
  const blocked = (!!a.childOf || !!b.childOf) && !done
  return {done, blocked}
}

/**
 * Resolves one seeded candidate (`{a, b, ...}`, codes only) to its live SKU
 * records plus the merge preview `combine([a, b])` would produce. Returns
 * `null` when either code no longer exists — renamed away via Fix code — so
 * the caller can skip the row instead of rendering something broken.
 *
 * `after` is computed via catalogueMath's own `combine`, the SAME function
 * the table and the merge drawer call. This row's "would roll up to" line
 * can therefore never disagree with what actually accepting the suggestion
 * would produce — recomputing it by hand here would be exactly the second,
 * divergent implementation Section 0 built `combine` to prevent.
 */
export function resolveCandidate(skus, candidate) {
  const byCode = new Map((skus || []).map((s) => [s.sku, s]))
  const a = byCode.get(candidate.a)
  const b = byCode.get(candidate.b)
  if (!a || !b) return null
  const after = combine([a, b])
  return {candidate, a, b, after, ...candidateStatus(a, b)}
}

const rowSx = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 1, flexWrap: 'wrap',
  py: 1, borderBottom: '1px solid rgba(145,158,171,0.16)',
}

/**
 * "Suggested mother-child merges" — Catalogue Task 5.
 *
 * THE HONESTY REQUIREMENT: `candidates` (MERGE_CANDIDATES from
 * data/noberoFreeze.js) is three deliberately SEEDED example pairs, not a
 * duplicate-detection result — this brand's catalogue has no genuine
 * duplicate-code pair to detect (see that file's own header comment for
 * exactly why). The warning banner below is the whole point of this
 * component: it must never be removed, weakened, or made dismissible, and
 * nothing in this file may infer a merge candidate from a code prefix or a
 * name similarity — every pairing shown here comes only from the seeded
 * `candidates` list.
 */
export default function MergeSuggestions({skus, candidates, onReviewMerge}) {
  const rows = React.useMemo(
    () => (candidates || []).map((c) => resolveCandidate(skus, c)).filter(Boolean),
    [skus, candidates],
  )

  if (rows.length === 0) return null

  return (
    <Box sx={sectionCardSx}>
      <Typography sx={{fontFamily: FONT, fontWeight: WEIGHT.bold, fontSize: SIZE.sm, color: NAVY}}>
        Suggested mother-child merges
      </Typography>
      <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>
        These codes look like the same item under a different nomenclature. Merging rolls their shipments and
        weights into one mother SKU.
      </Typography>

      <Alert severity='warning' sx={{mt: 1.25, fontFamily: FONT, fontSize: SIZE['2xs'], py: 0.75, '& .MuiAlert-message': {lineHeight: 1.55}}}>
        Seeded example. This brand&rsquo;s catalogue contains no genuine duplicate-code pair — its product names
        never collide, and the codes that do share a prefix turn out to be different sizes of the same design.
        These three suggestions are the two highest-shipping SKUs of three product groups, seeded so the merge
        flow can be tried at all. They are not a claim that either pair is one item.
      </Alert>

      <Box sx={{mt: 1.25}}>
        {rows.map(({candidate, a, b, after, done, blocked}) => (
          <Box key={candidate.a + '|' + candidate.b} sx={rowSx}>
            <Box sx={{minWidth: 0}}>
              <Typography sx={{fontFamily: 'monospace', fontWeight: WEIGHT.bold, fontSize: SIZE.xs, color: NAVY, wordBreak: 'break-all'}}>
                {a.sku}{' '}
                <Box component='span' sx={{color: SUBTLE, fontWeight: WEIGHT.regular}}>+</Box>{' '}
                {b.sku}
              </Typography>
              <Typography sx={{fontFamily: FONT, fontSize: SIZE['2xs'], color: SUBTLE, mt: 0.25}}>
                {int(a.ships)} + {int(b.ships)} shipments, would roll up to {int(after.ships)} at {kg(after.mpw)}
              </Typography>
            </Box>
            {done ? (
              <Chip size='small' label='Merged' sx={chipSx('ok')} />
            ) : blocked ? (
              <Chip size='small' label='Already mapped elsewhere' sx={chipSx('neutral')} />
            ) : (
              <Button size='small' sx={secondaryBtnSx} onClick={() => onReviewMerge([a.sku, b.sku])}>
                Review merge
              </Button>
            )}
          </Box>
        ))}
      </Box>
    </Box>
  )
}
